// This will need to be updated to support external language keys.
// The language file is temporary
import langFile from './en_US.json';

import parse, { type HTMLReactParserOptions } from 'html-react-parser';
import DOMPurify, { type Config } from 'dompurify';
import {
  createContext,
  useContext,
  useEffect,
  type FC,
  type ReactNode,
} from 'react';
import { clientLogger } from '../../utils/logger';

// Global store for translations (for non-component usage)
let globalTranslations: Record<string, string> = langFile;

export const setGlobalTranslations = (translations: Record<string, string>) => {
  globalTranslations = translations;
};

export const getGlobalTranslations = (): Record<string, string> => {
  return globalTranslations;
};

// Create a context for translations - hydrated externally
const LanguageContext = createContext<Record<string, string>>(langFile);

const DEFAULT_PURIFY_CONFIG: Config = {
  ADD_ATTR: ['target'],
};

type TokenCore = {
  replacementTokens?: { [key: string]: string };
  content?: string;
};

export type LanguageCore = {
  langKey?: string | string[];
  fallback?: string;
  purifyOptions?: Config;
} & TokenCore;

const tokenReplacement = ({
  content,
  replacementTokens: tokens = {},
}: TokenCore) => {
  if (!content) return null;
  return content.replace(/{{\s*([^{}\s]+)\s*}}/g, (_, key) => {
    return tokens[key] !== undefined ? tokens[key] : '';
  });
};

// Hook to access translations from context
export const useLanguageData = (): Record<string, string> => {
  return useContext(LanguageContext);
};

// Legacy function for non-component usage (e.g., validators)
// Now uses global translations which are kept in sync with the context
export const languageData = (): Record<string, string> => {
  return globalTranslations;
};

export const rawString = ({
  langKey,
  content: contentConfig,
  replacementTokens,
  fallback,
  purifyOptions,
  translations, // Optional: pass translations directly
}: LanguageCore & { translations?: Record<string, string> }) => {
  let baseText;

  // Use provided translations or fall back to global translations
  const language = translations || languageData();

  baseText = Array.isArray(langKey)
    ? langKey
        .map((key) => language[key])
        .find((value) => value != null && value !== '')
    : langKey
      ? language[langKey]
      : undefined;

  if (contentConfig) {
    baseText = contentConfig;
  }
  if (!baseText && fallback) {
    baseText = fallback;
  }

  if (!baseText) return undefined;

  const withReplacement = tokenReplacement({
    content: baseText.toString(),
    replacementTokens,
  });

  if (!withReplacement) return undefined;

  return DOMPurify.sanitize(withReplacement, {
    ...DEFAULT_PURIFY_CONFIG,
    ...purifyOptions,
  });
};

type LanguageProps = LanguageCore & {
  parseOptions?: HTMLReactParserOptions;
  purifyOptions?: Config;
};

export const Text: FC<LanguageProps> = ({
  langKey,
  content: contentConfig,
  fallback,
  replacementTokens = {},
  parseOptions = {},
  purifyOptions = {},
}: LanguageProps) => {
  // Get translations from context
  const translations = useLanguageData();

  if (!langKey && !contentConfig && !fallback) return null;

  const outputString = rawString({
    langKey,
    content: contentConfig,
    replacementTokens,
    fallback,
    purifyOptions,
    translations, // Pass context translations to rawString
  });

  if (!outputString) {
    return null;
  }

  try {
    return parse(outputString, parseOptions);
  } catch (error) {
    clientLogger.logError(error);
    return null;
  }
};

// Provider component to hydrate translations externally
interface LanguageProviderProps {
  translations: Record<string, string>;
  children: ReactNode;
}

export const LanguageProvider: FC<LanguageProviderProps> = ({
  translations,
  children,
}) => {
  // Keep global translations in sync with context for non-component usage
  useEffect(() => {
    setGlobalTranslations(translations);
  }, [translations]);

  return (
    <LanguageContext.Provider value={translations}>
      {children}
    </LanguageContext.Provider>
  );
};
